import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { BrandSelectorFooterProps, CoreBrandData } from '../../types';

const BrandSelectorFooter: React.FC<BrandSelectorFooterProps> = ({
  state,
  fieldKey,
  record,
  i,
  updateTreeSelectState,
  generateCoreBrandData,
  handleCoreBrandInputChange
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Cancel - reset temp values and close
    updateTreeSelectState(fieldKey, {
      tempSelectedBrands: state.selectedBrands,
      tempCustomName: state.customName,
      searchValue: ''
    });
    
    // Close the dropdown by faking the outside click antd listens for
    setTimeout(() => {
      const event = new MouseEvent('mousedown', { bubbles: true });
      document.dispatchEvent(event);
    }, 50);
  };

  // Apply needs at least one brand and a non-empty brand name
  const canApply = () => {
    const hasSelectedBrands = state.tempSelectedBrands && state.tempSelectedBrands.length > 0;
    const hasValidName = state.tempCustomName && state.tempCustomName.trim().length > 0;
    return hasSelectedBrands && hasValidName;
  };

  const handleApply = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!state.tempSelectedBrands || state.tempSelectedBrands.length === 0) {
      updateTreeSelectState(fieldKey, {
        hasError: true
      });
      return;
    }

    if (!state.tempCustomName || state.tempCustomName.trim() === '') {
      updateTreeSelectState(fieldKey, {
        hasError: true
      });
      return;
    }
    
    // Apply - save temp values
    updateTreeSelectState(fieldKey, {
      selectedBrands: state.tempSelectedBrands,
      customName: state.tempCustomName,
      searchValue: '',
      hasError: false
    });

    // Shape the selection the way the backend expects
    const finalData = generateCoreBrandData(state.tempCustomName, state.tempSelectedBrands);

    // The TreeSelect shows the custom name when one was entered
    const displayValue = state.tempCustomName || 'Custom Brand';

    const saveData = {
      displayName: state.tempCustomName || displayValue,
      brandData: finalData
    };
    
    try {
      handleCoreBrandInputChange(record.key, `coreBrand${i + 1}` as keyof CoreBrandData, JSON.stringify(saveData));
    } catch (error) {
      console.error('Error saving to form:', error);
    }

    // Close the dropdown by faking the outside click antd listens for
    setTimeout(() => {
      const event = new MouseEvent('mousedown', { bubbles: true });
      document.dispatchEvent(event);
    }, 50);
  };

  const handleDeleteConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Clear form field
    handleCoreBrandInputChange(record.key, `coreBrand${i + 1}` as keyof CoreBrandData, "");

    // Reset tree select state
    updateTreeSelectState(fieldKey, {
      selectedBrands: [],
      tempSelectedBrands: [],
      customName: '',
      tempCustomName: '',
      searchValue: '',
      hasError: false,
    });

    setDeleteConfirmOpen(false);

    // Close dropdown popup
    setTimeout(() => {
      const event = new MouseEvent('mousedown', { bubbles: true });
      document.dispatchEvent(event);
    }, 50);
  };

  const renderDeleteDropdown = () => {
    return (
      <div
        className="w-54 px-6 py-4 rounded-3xl bg-[#f0f0f0] border border-[#f5f5f5] flex flex-col gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base text-black/85 leading-8">Are you sure you want to delete?</div>
        <div className="flex justify-center gap-1">
          <button
              className="px-3 py-1.5 text-base text-[#f5222d] hover:text-[#f5222d]/80 rounded-full transition-colors cursor-pointer"
              onClick={(e) => {
                handleDeleteConfirm(e);
              }}
            >
              Delete
            </button>
            <button
              className="px-3 py-1.5 text-base text-black/85 hover:text-black/95 rounded-full transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
      </div>
    );
  };

  return (
    <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3">
      <Dropdown
        trigger={["click"]}
        placement="topLeft"
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
        }}
        popupRender={renderDeleteDropdown}
      >
        <button
          className="px-4 py-2 text-base text-[#f5222d] hover:text-red-600 hover:bg-red-50 rounded-full transition-colors cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Delete
        </button>
      </Dropdown>

      <div className="flex justify-end gap-3">
        <button
          className="px-4 py-2 text-base text-black/85 hover:text-black/95 rounded-full transition-colors cursor-pointer"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          className={`px-4 py-2 text-base rounded-full transition-colors cursor-pointer ${
            canApply()
              ? 'text-white bg-[#0E49B5] hover:bg-[#0E49B5]/80 cursor-pointer'
              : 'text-gray-400 bg-gray-200 cursor-not-allowed'
          }`}
          onClick={handleApply}
          disabled={!canApply()}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default BrandSelectorFooter;

